import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { VitalsModal } from './VitalsPanel';
import { usePatientPhoto } from './EMRPhotos';

const API = 'https://tcio-api.onrender.com';
const hdrs = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('tcio_token') } });

const PROOF_METHODS = [
  ['breathing_observed', 'Breathing observed'], ['movement_observed', 'Movement observed'],
  ['awake_responsive', 'Awake / responsive'], ['speaking', 'Speaking'],
  ['walking_active', 'Walking / active'], ['participating_activity', 'Participating in activity'],
  ['other_observable', 'Other observable sign'],
];
const BREATHING_DETAILS = [
  ['breathing_chest_rise', 'Chest rise/fall observed'],
  ['breathing_heard', 'Breath heard'],
  ['breathing_felt', 'Breath felt'],
];
const BREATHING_KEYS = BREATHING_DETAILS.map(([k]) => k);
const LOCATIONS = [
  ['bedroom', 'Bedroom'], ['living_room', 'Living Room'], ['kitchen', 'Kitchen'], ['bathroom', 'Bathroom'],
  ['outside_patio', 'Outside Patio'], ['group_room', 'Group Room'], ['dining_room', 'Dining'],
  ['office', 'Office'], ['med_room', 'Med Room'], ['off_site', 'Off-site'], ['other', 'Other'],
];
const OBS_STATUS = [
  ['sleeping', 'Sleeping'], ['awake', 'Awake'], ['in_group', 'In Group'], ['individual_session', 'Individual Session'],
  ['meal', 'Meal'], ['smoking', 'Smoking'], ['phone', 'Phone'], ['shower', 'Shower'],
  ['chores', 'Chores'], ['visiting', 'Visiting'], ['other', 'Other'],
];
const UTC_REASONS = [
  ['client_not_visible', 'Client not visible'], ['unable_to_access_location', 'Unable to access location'],
  ['client_off_site', 'Client off-site'], ['other', 'Other'],
];

const C = { ink: '#0f172a', sub: '#64748b', line: '#e2e8f0', blue: '#2563eb', red: '#dc2626',
  redBg: '#fef2f2', amber: '#d97706', amberBg: '#fffbeb', green: '#16a34a', greenBg: '#f0fdf4',
  purple: '#7c3aed', gray: '#94a3b8', soft: '#f8fafc' };

const chipStyle = (on) => ({
  padding: '8px 14px', borderRadius: 10, border: '1.5px solid ' + (on ? C.blue : C.line),
  background: on ? '#eff6ff' : '#fff', color: on ? C.blue : C.ink, fontSize: 13, fontWeight: 700,
  cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
});

function Chip({ on, onClick, children }) {
  return <span style={chipStyle(on)} onClick={onClick}>{children}{on ? ' ✓' : ''}</span>;
}

function Avatar({ patientId, photoKey, first, last, size = 44 }) {
  const url = usePatientPhoto(patientId, !!photoKey);
  const initials = ((first || ' ')[0] + (last || ' ')[0]).toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
      background: '#e2e8f0', border: '1px solid ' + C.line, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <span style={{ color: C.sub, fontWeight: 700, fontSize: size * 0.34 }}>{initials}</span>}
    </div>
  );
}

function Donut({ pct }) {
  const r = 52, cx = 64, cy = 64, circ = 2 * Math.PI * r;
  const val = pct == null ? 0 : Math.max(0, Math.min(100, pct));
  return (
    <svg width="128" height="128" viewBox="0 0 128 128">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f1f5f9" strokeWidth="14" />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={C.green} strokeWidth="14" strokeLinecap="round"
        strokeDasharray={(val / 100) * circ + ' ' + circ} transform={'rotate(-90 ' + cx + ' ' + cy + ')'} />
      <text x={cx} y={cy - 2} textAnchor="middle" fontSize="22" fontWeight="900" fill={C.ink}>
        {pct == null ? '—' : pct + '%'}</text>
      <text x={cx} y={cy + 18} textAnchor="middle" fontSize="10" fontWeight="700" fill={C.sub}>Compliance</text>
    </svg>
  );
}

function dueInfo(r) {
  if (r.overdue_minutes != null) return { badge: 'OVERDUE', color: C.red, bg: C.redBg, due: 'DUE NOW +' + r.overdue_minutes + 'm' };
  if (r.due_now) return { badge: 'DUE NOW', color: C.red, bg: C.redBg, due: 'DUE NOW' };
  if (r.due_soon && r.next_due) {
    const m = Math.max(0, Math.round((new Date(r.next_due).getTime() - Date.now()) / 60000));
    return { badge: 'DUE SOON', color: C.amber, bg: C.amberBg, due: m + ' min' };
  }
  return { badge: 'ON TIME', color: C.green, bg: C.greenBg,
    due: r.next_due ? new Date(r.next_due).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—' };
}

export default function Rounds({ user, initialFacilityName, openEpisodeId, onConsumedOpen }) {
  const [facilities, setFacilities] = useState([]);
  const [facility, setFacility] = useState('');
  const [board, setBoard] = useState([]);
  const [stats, setStats] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [tab, setTab] = useState('due');
  const [sel, setSel] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState('');
  const [vitalsFor, setVitalsFor] = useState(null);   // board row for the vitals modal
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 700;

  const OS_REASONS = [
    ['off_site_medical', 'Off-site medical care'], ['recovery_meeting', 'Recovery meeting'],
    ['community_integration', 'Community integration'], ['approved_pass', 'Approved pass'],
    ['work_school', 'Work / school'], ['left_facility', 'Left the facility (AMA/ACA)'], ['other', 'Other'],
  ];
  const BLANK = { mode: 'confirmed', methods: [], methodsOther: '', location: '', locationOther: '',
    obsStatus: '', obsStatusOther: '', notes: '', distress: false, alertSup: false,
    utcReason: '', utcOther: '', followup: '',
    osReason: '', osDetail: '', osVerified: '' };
  const [form, setForm] = useState({ ...BLANK });
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => { (async () => {
    try {
      const r = await axios.get(API + '/api/facilities?emr_live=1', hdrs());
      const raw = r.data.data || [];
      const fac = [
        ...[...raw].sort((a, b) => a.org_name.localeCompare(b.org_name)),
      ];
      setFacilities(fac);
      const pre = initialFacilityName && fac.find(x => x.org_name === initialFacilityName);
      setFacility(((pre || fac[0]) || {}).id || '');
    } catch (e) { setErr('Failed to load facilities'); }
  })(); }, [initialFacilityName]);

  const load = useCallback(async () => {
    if (!facility) return;
    try {
      const [b, s] = await Promise.all([
        axios.get(API + '/api/rounds/board?facilityId=' + facility, hdrs()),
        axios.get(API + '/api/rounds/stats?facilityId=' + facility, hdrs()).catch(() => null),
      ]);
      setBoard(b.data.data || []);
      setEnabled(!!b.data.rounds_enabled);
      if (s && s.data && s.data.ok) setStats(s.data.data);
    } catch (e) { setErr('Failed to load rounds board'); }
  }, [facility]);

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSel(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const [vitalsDue, setVitalsDue] = useState({});   // episode_id -> {due_now, overdue_minutes}
  useEffect(() => { (async () => {
    if (!facility) return;
    try {
      const r = await axios.get(API + '/api/emr/vitals/board?facilityId=' + facility, hdrs());
      const m = {};
      for (const row of (r.data.data || [])) m[row.episode_id] = { due_now: row.due_now, overdue_minutes: row.overdue_minutes };
      setVitalsDue(m);
    } catch (e) { /* silent */ }
  })(); }, [facility, savedFlash]);

  const openRound = (r) => { setSel(r); setForm({ ...BLANK }); setErr(''); };

  // Chart "Start Round" navigation: once the board loads, auto-open the requested episode
  const consumedOpen = React.useRef(false);
  useEffect(() => {
    if (!openEpisodeId || consumedOpen.current || !board.length) return;
    const row = board.find(r => r.episode_id === openEpisodeId);
    if (row) { consumedOpen.current = true; openRound(row); onConsumedOpen && onConsumedOpen(); }
  }, [board, openEpisodeId]);
  const closeRound = () => { setSel(null); setErr(''); };

  const counts = {
    due: board.filter(r => r.overdue_minutes != null || r.due_now).length,
    soon: board.filter(r => r.due_soon).length,
    exceptions: board.filter(r => r.last_status === 'exception').length,
  };

  const visible = board.filter(r => {
    if (tab === 'due') return r.overdue_minutes != null || r.due_now || r.due_soon;
    if (tab === 'completed') return r.last_observed_at && !r.due_now && r.overdue_minutes == null;
    return true;
  });

  const validate = () => {
    if (form.mode === 'confirmed') {
      if (!form.methods.length) return 'Select at least one proof-of-life method';
      if (form.methods.includes('breathing_observed') && !form.methods.some(m => BREATHING_KEYS.includes(m)))
        return 'How was breathing observed? Select chest rise/fall, heard, or felt';
      if (form.methods.includes('other_observable') && !form.methodsOther.trim()) return 'Describe the other observable sign';
      if (!form.location) return 'Select a location';
      if (form.location === 'other' && !form.locationOther.trim()) return 'Describe the other location';
      if (!form.obsStatus) return 'Select an observed status';
      if (form.obsStatus === 'other' && !form.obsStatusOther.trim()) return 'Describe the other observed status';
    } else if (form.mode === 'off_site') {
      if (!form.osReason) return 'Select why the client is off site';
      if (!form.osDetail.trim()) return 'Where is the client? Location/activity detail is required';
      if (form.osReason !== 'left_facility' && !form.osVerified.trim()) return 'How was the absence verified? (sign-out log, staff transport, pass log)';
    } else {
      if (!form.utcReason) return 'Select a reason';
      if (form.utcReason === 'other' && !form.utcOther.trim()) return 'Describe the reason';
      if (!form.followup.trim()) return 'Immediate follow-up action is required';
    }
    return null;
  };

  const save = async (andNext) => {
    const v = validate();
    if (v) { setErr(v); return; }
    setSaving(true); setErr('');
    try {
      const body = form.mode === 'confirmed' ? {
        episode_id: sel.episode_id, status: 'completed',
        proof_methods: form.methods, proof_methods_other: form.methodsOther,
        location: form.location, location_other: form.locationOther,
        observed_status: form.obsStatus, observed_status_other: form.obsStatusOther,
        notes: form.notes, appears_in_distress: form.distress, alert: form.alertSup,
        scheduled_due_at: sel.next_due || null,
      } : form.mode === 'off_site' ? {
        episode_id: sel.episode_id, status: 'off_site',
        off_site_reason: form.osReason, off_site_detail: form.osDetail.trim(),
        off_site_verified: form.osVerified.trim(), notes: form.notes,
        scheduled_due_at: sel.next_due || null,
      } : {
        episode_id: sel.episode_id, status: 'exception',
        unable_to_confirm_reason: form.utcReason, unable_to_confirm_other: form.utcOther,
        followup_action: form.followup, notes: form.notes,
        appears_in_distress: form.distress, scheduled_due_at: sel.next_due || null,
      };
      await axios.post(API + '/api/rounds/observations', body, hdrs());
      setSavedFlash(sel.first_name + ' ' + sel.last_name + ((form.mode === 'off_site' && form.osReason === 'left_facility') ? ' \u2014 supervisor alerted. Discharge the client if they are not returning.' : ''));
      setTimeout(() => setSavedFlash(''), (form.mode === 'off_site' && form.osReason === 'left_facility') ? 8000 : 2500);
      await load();
      if (andNext) {
        const remaining = board.filter(r => r.episode_id !== sel.episode_id && (r.overdue_minutes != null || r.due_soon));
        if (remaining.length) openRound(remaining[0]); else closeRound();
      } else closeRound();
    } catch (e) {
      setErr((e.response && e.response.data && e.response.data.error) || 'Save failed');
    } finally { setSaving(false); }
  };

  const inputStyle = { width: '100%', padding: 8, border: '1px solid ' + C.line, borderRadius: 8, boxSizing: 'border-box' };
  const secTitle = { fontWeight: 800, fontSize: 13, marginBottom: 8 };
  const star = <span style={{ color: C.red }}>*</span>;

  const FormBody = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <div style={secTitle}>1 · Proof of Life {star}</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ ...chipStyle(form.mode === 'confirmed'), borderColor: form.mode === 'confirmed' ? C.green : C.line, color: form.mode === 'confirmed' ? C.green : C.ink, background: form.mode === 'confirmed' ? C.greenBg : '#fff' }}
            onClick={() => f('mode', 'confirmed')}>Confirmed</span>
          <span style={{ ...chipStyle(form.mode === 'exception'), borderColor: form.mode === 'exception' ? C.amber : C.line, color: form.mode === 'exception' ? C.amber : C.ink, background: form.mode === 'exception' ? C.amberBg : '#fff' }}
            onClick={() => f('mode', 'exception')}>Unable to Confirm</span>
          <span style={{ ...chipStyle(form.mode === 'off_site'), borderColor: form.mode === 'off_site' ? C.blue : C.line, color: form.mode === 'off_site' ? C.blue : C.ink, background: form.mode === 'off_site' ? '#EAF1F8' : '#fff' }}
            onClick={() => f('mode', 'off_site')}>Client Off Site</span>
        </div>
        {form.mode === 'off_site' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Why is the client off site? {star}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {OS_REASONS.map(([k, l]) => <Chip key={k} on={form.osReason === k} onClick={() => f('osReason', k)}>{l}</Chip>)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Where is the client / what are they doing? {star}</div>
              <textarea style={inputStyle} rows={2} value={form.osDetail}
                placeholder="e.g. Cardiology appointment at Mission Hospital, staff transport, expected back 2 PM"
                onChange={e => f('osDetail', e.target.value)} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>How was the absence verified? {star}</div>
              <input style={inputStyle} value={form.osVerified}
                placeholder="e.g. Sign-out log, staff transport roster, pass approval"
                onChange={e => f('osVerified', e.target.value)} />
            </div>
            <div style={{ fontSize: 12, color: C.inkSoft }}>
              Documented off-site satisfies this round without a supervisor alert. Use Unable to Confirm if the client's whereabouts are NOT known.
            </div>
          </div>
        ) : form.mode === 'confirmed' ? (
          <div>
            <div style={{ fontSize: 12, color: C.sub, marginBottom: 6 }}>How was proof of life confirmed? Select at least one</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {PROOF_METHODS.map(([k, l]) => (
                <Chip key={k} on={form.methods.includes(k)}
                  onClick={() => f('methods', form.methods.includes(k)
                    ? form.methods.filter(m => m !== k && !(k === 'breathing_observed' && BREATHING_KEYS.includes(m)))
                    : [...form.methods, k])}>{l}</Chip>
              ))}
            </div>
            {form.methods.includes('breathing_observed') && (
              <div style={{ marginTop: 8, marginLeft: 14, paddingLeft: 10, borderLeft: '3px solid ' + C.line }}>
                <div style={{ fontSize: 12, color: C.sub, marginBottom: 5 }}>How was breathing observed? *</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {BREATHING_DETAILS.map(([k, l]) => (
                    <Chip key={k} on={form.methods.includes(k)}
                      onClick={() => f('methods', form.methods.includes(k) ? form.methods.filter(m => m !== k) : [...form.methods, k])}>{l}</Chip>
                  ))}
                </div>
              </div>
            )}
            {form.methods.includes('other_observable') && (
              <input value={form.methodsOther} onChange={e => f('methodsOther', e.target.value)} placeholder="Describe..." style={{ ...inputStyle, marginTop: 8 }} />
            )}
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 12, color: C.amber, fontWeight: 700, marginBottom: 6 }}>Requires immediate follow-up</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {UTC_REASONS.map(([k, l]) => <Chip key={k} on={form.utcReason === k} onClick={() => f('utcReason', k)}>{l}</Chip>)}
            </div>
            {form.utcReason === 'other' && (
              <input value={form.utcOther} onChange={e => f('utcOther', e.target.value)} placeholder="Describe reason..." style={{ ...inputStyle, marginTop: 8 }} />
            )}
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, color: C.sub, marginBottom: 4 }}>Immediate follow-up action taken {star}</div>
              <input value={form.followup} onChange={e => f('followup', e.target.value)} placeholder="e.g. Notified supervisor, searching grounds..." style={inputStyle} />
            </div>
          </div>
        )}
      </div>

      {form.mode === 'confirmed' && (
        <div>
          <div style={secTitle}>2 · Location {star}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {LOCATIONS.map(([k, l]) => <Chip key={k} on={form.location === k} onClick={() => f('location', k)}>{l}</Chip>)}
          </div>
          {form.location === 'other' && (
            <input value={form.locationOther} onChange={e => f('locationOther', e.target.value)} placeholder="Describe..." style={{ ...inputStyle, marginTop: 8 }} />
          )}
        </div>
      )}

      {form.mode === 'confirmed' && (
        <div>
          <div style={secTitle}>3 · Observed Status {star}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {OBS_STATUS.map(([k, l]) => <Chip key={k} on={form.obsStatus === k} onClick={() => f('obsStatus', k)}>{l}</Chip>)}
          </div>
          {form.obsStatus === 'other' && (
            <input value={form.obsStatusOther} onChange={e => f('obsStatusOther', e.target.value)} placeholder="Describe..." style={{ ...inputStyle, marginTop: 8 }} />
          )}
        </div>
      )}

      <div>
        <div style={secTitle}>4 · Notes (optional)</div>
        <textarea value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Add notes or details about this round..."
          rows={2} style={{ ...inputStyle, fontFamily: 'inherit' }} />
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.distress} onChange={e => f('distress', e.target.checked)} />
          Appears in distress <span style={{ color: C.sub, fontSize: 12 }}>(distressed or not at baseline)</span>
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.mode === 'exception' ? true : form.alertSup} disabled={form.mode === 'exception'}
            onChange={e => f('alertSup', e.target.checked)} />
          Alert Supervisor — needs attention {form.mode === 'exception' && <span style={{ color: C.amber, fontSize: 12 }}>(auto for exceptions)</span>}
        </label>
      </div>
    </div>
  );

  const di = sel ? dueInfo(sel) : null;

  const statRow = (icon, label, val, color) => (
    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ fontSize: 13, color: C.ink }}>{icon} {label}</span>
      <span style={{ fontWeight: 900, fontSize: 14, color: color || C.ink }}>{val}</span>
    </div>
  );

  return (
    <div style={{ padding: isMobile ? 12 : 24, maxWidth: 1400, margin: '0 auto', color: C.ink }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Rounds</h2>
        <select value={facility} onChange={e => setFacility(e.target.value)}
          style={{ padding: 8, borderRadius: 8, border: '1px solid ' + C.line }}>
          {facilities.map(x => <option key={x.id} value={x.id}>{x.org_name}</option>)}
        </select>
        <div style={{ background: C.redBg, color: C.red, borderRadius: 10, padding: '6px 14px', fontWeight: 800 }}>Due Now {counts.due}</div>
        <div style={{ background: C.amberBg, color: C.amber, borderRadius: 10, padding: '6px 14px', fontWeight: 800 }}>Due Soon {counts.soon}</div>
      </div>

      {!enabled && <div style={{ background: C.amberBg, color: C.amber, padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
        Rounds timing is not enabled for this facility — observations can still be recorded.</div>}
      {savedFlash && <div style={{ background: C.greenBg, color: C.green, padding: 10, borderRadius: 8, marginBottom: 12, fontWeight: 700 }}>
        ✓ Round saved — {savedFlash}</div>}

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
        {/* main column */}
        <div style={{ flex: 1, minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {[['due', 'Due Now (' + (counts.due + counts.soon) + ')'], ['all', 'All Clients'], ['completed', 'Completed'], ['missed', 'Missed'], ['late', 'Late']].map(([k, l]) => (
              <span key={k} onClick={() => setTab(k)} style={{ ...chipStyle(tab === k), borderRadius: 8 }}>{l}</span>
            ))}
          </div>

          {(tab === 'missed' || tab === 'late') && <RoundMissPanel facility={facility} kind={tab} user={user} />}
          <div style={{ background: '#fff', border: '1px solid ' + C.line, borderRadius: 14, overflow: 'hidden', display: (tab === 'missed' || tab === 'late') ? 'none' : undefined }}>
            {!isMobile && (
              <div style={{ display: 'grid', gridTemplateColumns: '2.2fr 0.9fr 1.1fr 1fr 1.1fr 1fr', padding: '10px 16px',
                background: C.soft, borderBottom: '1px solid ' + C.line, fontSize: 11, fontWeight: 800, color: C.sub, letterSpacing: 0.5 }}>
                <div>CLIENT</div><div>FREQUENCY</div><div>NEXT DUE</div><div>LAST ROUND</div><div>STATUS</div><div style={{ textAlign: 'right' }}>ACTIONS</div>
              </div>
            )}
            {visible.length === 0 && <div style={{ color: C.sub, padding: 24, textAlign: 'center' }}>Nothing here — all caught up.</div>}
            {visible.map(r => {
              const d = dueInfo(r);
              const lastT = r.last_observed_at ? new Date(r.last_observed_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—';
              return isMobile ? (
                <div key={r.episode_id} onClick={() => openRound(r)}
                  style={{ padding: 12, borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                    background: (d.badge === 'OVERDUE' || d.badge === 'DUE NOW') ? C.redBg : '#fff', display: 'flex', gap: 10, alignItems: 'center' }}>
                  <Avatar patientId={r.patient_id} photoKey={r.photo_key} first={r.first_name} last={r.last_name} size={40} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800 }}>{r.last_name}, {r.first_name}</div>
                    <div style={{ fontSize: 12, color: C.sub }}>{(r.house_name || r.loc || '')} · {r.level_code || '—'} · last {lastT}
                      {r.last_status === 'exception' && <span style={{ color: C.purple, fontWeight: 700 }}> · EXCEPTION</span>}</div>
                  </div>
                  <div style={{ color: d.color, fontWeight: 800, fontSize: 12, textAlign: 'right' }}>{d.due}</div>
                </div>
              ) : (
                <div key={r.episode_id}
                  style={{ display: 'grid', gridTemplateColumns: '2.2fr 0.9fr 1.1fr 1fr 1.1fr 1fr', padding: '12px 16px',
                    alignItems: 'center', borderBottom: '1px solid #f1f5f9',
                    background: (d.badge === 'OVERDUE' || d.badge === 'DUE NOW') ? C.redBg : '#fff',
                    borderLeft: d.badge === 'OVERDUE' ? '3px solid ' + C.red : '3px solid transparent' }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                    <Avatar patientId={r.patient_id} photoKey={r.photo_key} first={r.first_name} last={r.last_name} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800 }}>{r.last_name}, {r.first_name}</div>
                      <div style={{ fontSize: 12, color: C.sub }}>{(r.house_name || r.loc || '')}
                        {r.level_code && <span style={{ marginLeft: 6, background: '#eef2ff', color: '#4338ca', borderRadius: 6, padding: '1px 7px', fontSize: 11, fontWeight: 800 }}>{r.level_code}</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontWeight: 700 }}>{r.level_code || '—'}</div>
                  <div style={{ color: d.badge === 'ON TIME' ? C.ink : d.color, fontWeight: 800, fontSize: 13 }}>{d.due}</div>
                  <div style={{ fontSize: 13 }}>{lastT}<div style={{ fontSize: 11, color: C.sub }}>{r.last_observed_at ? 'Today' : ''}</div></div>
                  <div>
                    <span style={{ background: d.bg, color: d.color, border: '1px solid ' + d.color, borderRadius: 8, padding: '3px 10px', fontSize: 11, fontWeight: 800 }}>{d.badge}</span>
                    {r.last_status === 'exception' && <div style={{ color: C.purple, fontSize: 11, fontWeight: 800, marginTop: 3 }}>• EXCEPTION</div>}
                  </div>
                  <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {(() => {
                      const vd = vitalsDue[r.episode_id];
                      const vDue = vd && (vd.due_now || vd.overdue_minutes != null);
                      return (
                        <button onClick={() => setVitalsFor(r)}
                          style={{ background: vDue ? C.redBg : '#fff', color: vDue ? C.red : C.blue,
                            border: '1.5px solid ' + (vDue ? C.red : C.blue), borderRadius: 9, padding: '8px 12px',
                            fontWeight: 800, fontSize: 13, cursor: 'pointer', marginRight: 6 }}>
                          ❤ {vDue ? 'Vitals DUE' : 'Vitals'}</button>
                      );
                    })()}
                    <button onClick={() => openRound(r)}
                      style={{ background: C.blue, color: '#fff', border: 'none', borderRadius: 9, padding: '9px 16px', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
                      Start Round</button>
                  </div>
                </div>
              );
            })}
            {tab !== 'completed' && (
              <div style={{ padding: 12, textAlign: 'center' }}>
                <button onClick={() => setTab('completed')}
                  style={{ background: '#fff', color: C.blue, border: '1px solid ' + C.blue, borderRadius: 9, padding: '8px 18px', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
                  View Completed Rounds</button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 18, justifyContent: 'center', padding: 14, fontSize: 12, color: C.sub, flexWrap: 'wrap' }}>
            {[[C.green, 'On Time'], [C.amber, 'Due Soon'], [C.red, 'Overdue'], [C.purple, 'Exception'], [C.gray, 'Completed']].map(([c, l]) => (
              <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: c, display: 'inline-block' }} />{l}</span>
            ))}
          </div>
        </div>

        {/* right sidebar */}
        {!isMobile && (
          <div style={{ flex: '0 0 300px' }}>
            <div style={{ background: '#fff', border: '1px solid ' + C.line, borderRadius: 14, padding: 18, marginBottom: 16 }}>
              <div style={{ fontWeight: 900, fontSize: 15, marginBottom: 10 }}>Round Summary</div>
              {statRow('👥', 'Total Clients', board.length)}
              {statRow('🕐', 'Due Now', counts.due, C.red)}
              {statRow('🕑', 'Due Soon (<30 min)', counts.soon, C.amber)}
              {statRow('✅', 'Completed (Today)', stats ? stats.completed_today : '—', C.green)}
              {statRow('⚠️', 'Exceptions', stats ? stats.exceptions_week : counts.exceptions, C.purple)}
              {statRow('⏱', 'On Time (Last 7 Days)', stats && stats.on_time_pct != null ? stats.on_time_pct + '%' : '—', C.green)}
              {statRow('🛡', 'Compliance (This Week)', stats && stats.compliance_pct != null ? stats.compliance_pct + '%' : '—', C.green)}
            </div>
            <div style={{ background: '#fff', border: '1px solid ' + C.line, borderRadius: 14, padding: 18 }}>
              <div style={{ fontWeight: 900, fontSize: 15, marginBottom: 6 }}>Round Compliance <span style={{ fontWeight: 500, fontSize: 12, color: C.sub }}>(This Week)</span></div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <Donut pct={stats ? stats.compliance_pct : null} />
                <div style={{ fontSize: 12 }}>
                  {[[C.green, 'On Time', stats && stats.on_time_pct], [C.amber, 'Late', stats && stats.late_pct], [C.red, 'Exception', stats && stats.exception_pct]].map(([c, l, v]) => (
                    <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: c, display: 'inline-block' }} />
                      <span style={{ flex: 1 }}>{l}</span>
                      <span style={{ fontWeight: 800 }}>{v != null ? v + '%' : '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {vitalsFor && (
        <VitalsModal episodeId={vitalsFor.episode_id}
          patientName={vitalsFor.last_name + ', ' + vitalsFor.first_name}
          onClose={() => setVitalsFor(null)}
          onSaved={() => { setSavedFlash(vitalsFor.first_name + ' ' + vitalsFor.last_name + ' — vitals'); setTimeout(() => setSavedFlash(''), 2500); }} />
      )}

      {/* complete round modal */}
      {sel && (
        <div onClick={closeRound} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000,
          display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: isMobile ? '16px 16px 0 0' : 16,
            width: '100%', maxWidth: isMobile ? '100%' : 960, maxHeight: isMobile ? '92vh' : '88vh', overflowY: 'auto',
            boxShadow: '0 24px 64px rgba(15,23,42,0.35)', display: 'flex', flexDirection: 'row' }}>
            <div style={{ flex: 1, padding: isMobile ? 16 : 24, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ fontWeight: 900, fontSize: 15, color: C.sub, letterSpacing: 0.3 }}>Complete Round</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {!isMobile && <span style={{ fontSize: 11, color: C.sub, border: '1px solid ' + C.line, borderRadius: 6, padding: '2px 6px' }}>ESC</span>}
                  <span onClick={closeRound} style={{ cursor: 'pointer', color: C.sub, fontWeight: 800, fontSize: 16 }}>✕</span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Avatar patientId={sel.patient_id} photoKey={sel.photo_key} first={sel.first_name} last={sel.last_name} size={52} />
                  <div>
                    <div style={{ fontWeight: 900, fontSize: 17 }}>{sel.last_name}, {sel.first_name}</div>
                    <div style={{ fontSize: 12, color: C.sub }}>
                      {(sel.house_name || sel.loc || '')} · {sel.level_code || ''}
                      {sel.next_due && ' · Next due: ' + new Date(sel.next_due).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
                {di && <div style={{ color: di.color, background: di.bg, fontWeight: 800, fontSize: 12, padding: '4px 10px', borderRadius: 8, whiteSpace: 'nowrap' }}>{di.due}</div>}
              </div>

              {FormBody}

              {err && <div style={{ color: C.red, background: C.redBg, padding: 10, borderRadius: 8, marginTop: 12, fontWeight: 700 }}>{err}</div>}

              <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end',
                position: isMobile ? 'sticky' : 'static', bottom: 0, background: '#fff', paddingTop: 8, paddingBottom: isMobile ? 8 : 0 }}>
                <button disabled={saving} onClick={() => save(true)}
                  style={{ padding: '10px 18px', borderRadius: 10, border: '1px solid ' + C.blue, background: '#fff', color: C.blue, fontWeight: 800, cursor: 'pointer' }}>
                  Save & Add Another</button>
                <button disabled={saving} onClick={() => save(false)}
                  style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: form.mode === 'exception' ? C.amber : C.blue, color: '#fff', fontWeight: 800, cursor: 'pointer' }}>
                  {saving ? 'Saving...' : (form.mode === 'exception' ? 'Save Exception' : 'Save Round')}</button>
              </div>

              <div style={{ marginTop: 12, background: C.greenBg, color: C.green, borderRadius: 8, padding: 8, fontSize: 12 }}>
                🛡 Audit protected — this round will be timestamped and cannot be altered.
              </div>
            </div>
            {!isMobile && (
              <div style={{ flex: '0 0 240px', borderLeft: '1px solid ' + C.line, background: C.soft, padding: 20, borderRadius: '0 16px 16px 0' }}>
                <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 14 }}>Round Summary</div>
                {[['Client', sel.last_name + ', ' + sel.first_name],
                  ['House', sel.house_name || sel.loc || '—'],
                  ['Frequency', sel.level_code || '—'],
                  ['Scheduled Due', sel.next_due ? new Date(sel.next_due).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—'],
                  ['Documented By', (user && (user.first_name ? user.first_name + ' ' + (user.last_name || '') : user.email)) || '—'],
                  ['Role', (user && user.role) || '—'],
                ].map(([k, v]) => (
                  <div key={k} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: C.sub, fontWeight: 700 }}>{k}</div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{v}</div>
                  </div>
                ))}
                <div style={{ marginTop: 14, background: C.greenBg, color: C.green, borderRadius: 10, padding: 10, fontSize: 11, fontWeight: 700 }}>
                  ✓ Audit Protected<br /><span style={{ fontWeight: 500 }}>Timestamped · cannot be altered</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// Chad 11/4: every staff member sees every missed and late round. Missed = no round ever logged for the window.
// Late = a round was logged after the window closed. Managers may acknowledge with a reason; the row stays.
function RoundMissPanel({ facility, kind, user }) {
  const [data, setData] = useState({ missed: [], late: [], by_owner: [] });
  const [days, setDays] = useState(7);
  const [ackFor, setAckFor] = useState(null);
  const [reason, setReason] = useState('');
  const canAck = ['ceo', 'admin', 'super_admin', 'program_manager', 'operations', 'clinical_director'].includes(String((user && user.role) || ''));
  const load = useCallback(async () => {
    if (!facility) return;
    const to = new Date(); const from = new Date(Date.now() - (days - 1) * 86400000);
    const iso = d => d.toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
    try { const r = await axios.get(API + '/api/rounds/misses?facilityId=' + facility + '&from=' + iso(from) + '&to=' + iso(to), hdrs()); setData(r.data || { missed: [], late: [], by_owner: [] }); }
    catch (e) { setData({ missed: [], late: [], by_owner: [] }); }
  }, [facility, days]);
  useEffect(() => { load(); }, [load]);
  const rows = kind === 'missed' ? data.missed : data.late;
  const fmt = t => t ? new Date(t).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
  const mins = m => m == null ? '—' : (m >= 60 ? Math.floor(m / 60) + 'h ' + (m % 60) + 'm' : m + 'm');
  const ack = async (id) => {
    if (!reason.trim()) return;
    try { await axios.post(API + '/api/rounds/misses/' + id + '/ack', { reason }, hdrs()); setAckFor(null); setReason(''); load(); } catch (e) { alert((e.response && e.response.data && e.response.data.error) || 'Failed'); }
  };
  return (
    <div style={{ background: '#fff', border: '1px solid ' + C.line, borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid ' + C.line, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 800, color: kind === 'missed' ? C.red : '#b45309', fontSize: 15 }}>
          {kind === 'missed' ? 'Missed rounds' : 'Late rounds'} — {rows.length}
        </div>
        <div style={{ fontSize: 12, color: C.sub }}>{kind === 'missed' ? 'No round was logged for the window.' : 'Round logged after the window closed.'} Visible to all staff.</div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {[1, 7, 30].map(d => <span key={d} onClick={() => setDays(d)} style={{ ...chipStyle(days === d), borderRadius: 8 }}>{d === 1 ? 'Today' : d + ' days'}</span>)}
        </div>
      </div>
      {data.by_owner.length > 0 && (
        <div style={{ display: 'flex', gap: 8, padding: '10px 16px', flexWrap: 'wrap', borderBottom: '1px solid ' + C.line, background: '#f8fafc' }}>
          {data.by_owner.map(o => (
            <span key={o.owner} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 999, background: '#fff', border: '1px solid ' + C.line }}>
              <b>{o.owner}</b> · <span style={{ color: C.red }}>{o.missed} missed</span> · <span style={{ color: '#b45309' }}>{o.late} late</span>
            </span>
          ))}
        </div>
      )}
      {rows.length === 0 ? (
        <div style={{ padding: 28, textAlign: 'center', color: C.sub }}>None in this range.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: '#f8fafc', color: C.sub, textAlign: 'left' }}>
              <th style={{ padding: '8px 12px' }}>Client</th><th style={{ padding: '8px 12px' }}>House</th><th style={{ padding: '8px 12px' }}>Level</th>
              <th style={{ padding: '8px 12px' }}>Due</th>{kind === 'late' && <th style={{ padding: '8px 12px' }}>Logged</th>}
              <th style={{ padding: '8px 12px' }}>{kind === 'missed' ? 'Overdue by' : 'Late by'}</th><th style={{ padding: '8px 12px' }}>On shift</th><th style={{ padding: '8px 12px' }}>Reviewed</th>
            </tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id} style={{ borderTop: '1px solid ' + C.line, background: r.acknowledged_at ? '#fff' : (kind === 'missed' ? '#fff5f5' : '#fffbeb') }}>
                <td style={{ padding: '8px 12px', fontWeight: 700 }}>{r.last_name}, {r.first_name}</td>
                <td style={{ padding: '8px 12px' }}>{r.house || '—'}</td>
                <td style={{ padding: '8px 12px' }}>{r.level_code || '—'}{r.interval_minutes ? ' · q' + r.interval_minutes + 'm' : ''}</td>
                <td style={{ padding: '8px 12px' }}>{fmt(r.due_at)}</td>
                {kind === 'late' && <td style={{ padding: '8px 12px' }}>{fmt(r.done_late_at)}</td>}
                <td style={{ padding: '8px 12px', fontWeight: 700, color: kind === 'missed' ? C.red : '#b45309' }}>{mins(r.minutes_late)}</td>
                <td style={{ padding: '8px 12px', fontWeight: 700 }}>{r.owner_name || <span style={{ color: C.sub, fontWeight: 400 }}>no one on shift</span>}</td>
                <td style={{ padding: '8px 12px' }}>
                  {r.acknowledged_at ? <span style={{ color: C.sub }} title={r.ack_reason}>{r.acknowledged_by_name} · {fmt(r.acknowledged_at)}</span>
                    : canAck ? (ackFor === r.id ? (
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason" style={{ fontSize: 12, padding: '4px 8px', border: '1px solid ' + C.line, borderRadius: 6, width: 180 }} />
                        <button onClick={() => ack(r.id)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: 'none', background: C.blue, color: '#fff', fontWeight: 700 }}>Save</button>
                        <button onClick={() => { setAckFor(null); setReason(''); }} style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid ' + C.line, background: '#fff' }}>Cancel</button>
                      </span>
                    ) : <button onClick={() => setAckFor(r.id)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid ' + C.line, background: '#fff' }}>Acknowledge</button>)
                    : <span style={{ color: C.red, fontWeight: 700 }}>Unreviewed</span>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
